import type { Result } from '~/utils/result'
import type { SolaDatabase } from './db'
import type { StorageError } from './errors'
import { CURRENT_SCHEMA, type StoreName } from './schema'
import { type SolaTransaction, withTransaction } from './transactions'

/**
 * Clearing every store, in one place.
 *
 * Two callers need this and they need it differently. A **restore** must clear
 * and rewrite inside a single transaction, so it cannot call something that
 * opens its own. A **reset** has nothing to write afterwards. So the primitive
 * is `clearAllStoresIn`, which operates on a transaction the caller already
 * opened, and `clearAllStores` is the convenience wrapper around it.
 *
 * Extracting this matters because a partial wipe reported as success is the
 * worst outcome available on either path: a restore that half-cleared would
 * merge two datasets, and a reset that half-cleared would leave the app
 * claiming a fresh start it did not perform.
 */

/**
 * Every store a full wipe walks, derived from the schema rather than listed by
 * hand.
 *
 * Deriving it is the point, and schema v2 is the case it was written for:
 * adding `projects` (D-062) taught both the restore and the reset about it
 * without either being edited. A hand-maintained list is exactly the kind of
 * thing that is updated in one place and forgotten in the other.
 *
 * `CURRENT_SCHEMA`, never `SCHEMA_V1` — a wipe that skipped the newest store
 * would leave the user's project history behind a reset that reported success.
 */
export const ALL_STORE_NAMES: readonly StoreName[] = CURRENT_SCHEMA.map(
  (definition) => definition.name,
)

/** Records per store, captured before they were removed. */
export type StoreCounts = Readonly<Record<string, number>>

/** One read-write transaction spanning every store. */
export type AllStoresTransaction = SolaTransaction<StoreName[]>

/**
 * Clear every store inside a transaction the caller already opened.
 *
 * Counts are read **before** each clear and inside the same transaction, so the
 * number reported to the user cannot disagree with what was actually removed —
 * and cannot be inflated by a concurrent write, because there is no await on
 * anything but IndexedDB requests between the count and the clear.
 */
export async function clearAllStoresIn(tx: AllStoresTransaction): Promise<StoreCounts> {
  const counts: Record<string, number> = {}

  for (const name of ALL_STORE_NAMES) {
    const store = tx.objectStore(name)
    counts[name] = await store.count()
    await store.clear()
  }

  return counts
}

/**
 * Clear every store atomically.
 *
 * `withTransaction` supplies abort-on-throw and the typed `Result`, so a
 * failure part-way through leaves the database exactly as it was.
 */
export async function clearAllStores(db: SolaDatabase): Promise<Result<StoreCounts, StorageError>> {
  return withTransaction(db, [...ALL_STORE_NAMES], (tx) => clearAllStoresIn(tx))
}
